import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';
import { ITopic } from 'src/app/core/models/i-topic';
import { ITopicBase, TopicBase } from 'src/app/core/models/i-topic-base';
import { IContentList } from '../../core/models/i-content-list';
import { ContentListStateService } from '../shared/services/cmsapi/content-list-state.service';
import { ITag, Tag } from 'src/app/core/models/i-tag';
import { TagService } from 'src/app/content-management/shared/services/tag.service';
import { TagType } from 'src/app/content-management/shared/enums/tag-type';
import { ITopicWithTranslations } from 'src/app/core/models/i-topic-with-translations';

@Component({
  selector: 'app-topics-on-content-list',
  templateUrl: './add-topics-to-content-list.component.html',
})
export class AddTopicsToContentListComponent implements OnInit, OnDestroy {
  @Input() contentList: IContentList;
  @Output() triggerTopicChangeToContentList = new EventEmitter<ITopicBase[]>();
  @Output() triggerTagChangeToContentList = new EventEmitter<ITag[]>();

  topicDataSource: MatTableDataSource<ITopicBase> = new MatTableDataSource();
  displayedColumns: string[] = ['name', 'remove'];
  topicResults: ITopicWithTranslations[] = null;
  searchTopicText = new UntypedFormControl();
  selectedTopics = [];

  tagDataSource: MatTableDataSource<ITopicBase> = new MatTableDataSource();
  tagResults: ITag[] = null;
  searchTagText = new UntypedFormControl();
  selectedTags = [];

  constructor(
    public topicService: TopicService,
    public tagService: TagService,
    public contentListStateService: ContentListStateService,
  ) {}
  pageSubscriptions: Subscription[] = [];

  ngOnInit(): void {
    if (this.contentList.topics) {
      this.selectedTopics = this.contentList.topics;
      this.topicDataSource.data = this.selectedTopics;
    }

    if (this.contentList.tags) {
      this.selectedTags = this.contentList.tags;
      this.tagDataSource.data = this.selectedTags;
    }

    this.searchTopicText.valueChanges.pipe(debounceTime(500)).subscribe((x) => {
      if (x != '') {
        const topicServiceSubscription = this.topicService
          .getFiltered$(
            this.contentList.deliveryTargetCodes.join(','),
            x,
            10,
            true,
            true,
          )
          .pipe(debounceTime(500))
          .subscribe((x) => (this.topicResults = x));
        this.pageSubscriptions.push(topicServiceSubscription);
      } else {
        this.topicResults = null;
      }
    });

    this.searchTagText.valueChanges.pipe(debounceTime(500)).subscribe((x) => {
      if (x != '') {
        const tagServiceSubscription = this.tagService
          .get$(x, TagType.Soft)
          .pipe(debounceTime(500))
          .subscribe((response) => (this.tagResults = response.results));
        this.pageSubscriptions.push(tagServiceSubscription);
      } else {
        this.tagResults = null;
      }
    });
  }

  ngOnDestroy(): void {
    this.pageSubscriptions.forEach((subscription) =>
      subscription.unsubscribe(),
    );
  }

  addTopic(topic: TopicBase): void {
    if (!this.selectedTopics.some((y) => y === topic)) {
      this.selectedTopics.push(topic);
      this.topicDataSource.data = this.selectedTopics;
      this.triggerTopicChangeToContentList.emit(this.selectedTopics);
      this.contentListStateService.changeContentList(true);

      this.searchTopicText.setValue('');
    }
  }

  addTag(tag: Tag): void {
    if (!this.selectedTags.some((y) => y === tag)) {
      this.selectedTags.push(tag);
      this.tagDataSource.data = this.selectedTags;
      this.triggerTagChangeToContentList.emit(this.selectedTags);
      this.contentListStateService.changeContentList(true);

      this.searchTagText.setValue('');
    }
  }

  removeTopic(topic: TopicBase): void {
    this.selectedTopics = this.selectedTopics.filter((x) => x !== topic);
    this.topicDataSource.data = this.selectedTopics;
    this.triggerTopicChangeToContentList.emit(this.selectedTopics);
    this.contentListStateService.changeContentList(true);
  }

  removeTag(tag: Tag): void {
    this.selectedTags = this.selectedTags.filter((x) => x !== tag);
    this.tagDataSource.data = this.selectedTags;
    this.triggerTagChangeToContentList.emit(this.selectedTags);
    this.contentListStateService.changeContentList(true);
  }

  isTopicSelected(topic: TopicBase): boolean {
    return this.selectedTopics.some((x) => x.id === topic.id);
  }

  isTagSelected(tag: Tag): boolean {
    return this.selectedTags.some((x) => x.id === tag.id);
  }
}
